import React, { useState } from "react";
import { Link } from "react-router-dom";
import { TopBar } from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

export function Pricing() {
  const tiers = [
    { name: "Starter", price: "$299", period: "/month", features: ["App access", "Personalised calorie plan", "Weekly check-in review", "Pacific food library"] },
    { name: "Warrior", price: "$499", period: "/month", features: ["Everything in Starter", "Custom workout programme", "Weekly video review", "Unlimited messaging", "Progress photos"], featured: true },
    { name: "12-Week Transformation", price: "$1,799", period: "once", features: ["Full 12-week programme", "All Warrior features", "Mid & end review calls", "Transformation plan after week 12"] },
  ];
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5 py-16">
        <div className="text-gold uppercase tracking-[0.3em] text-xs">Pricing</div>
        <h1 className="font-display font-black uppercase text-5xl tracking-tighter">Invest in yourself.</h1>
        <p className="text-neutral-400 mt-2 max-w-xl">All packages priced in NZD. Secure payments coming soon — for now, apply and we'll process payment manually.</p>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {tiers.map((t,i) => (
            <div key={i} className={`kw-card p-6 ${t.featured ? "border-gold" : ""}`} data-testid={`tier-${i}`}>
              {t.featured && <div className="text-[10px] text-gold uppercase tracking-widest">Most popular</div>}
              <h3 className="font-display font-black uppercase text-3xl mt-2 tracking-tighter">{t.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display font-black text-4xl text-gold">{t.price}</span>
                <span className="text-neutral-500 text-sm">NZD {t.period}</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-neutral-300">
                {t.features.map((f,j)=>(<li key={j} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-gold mt-0.5"/>{f}</li>))}
              </ul>
              <Link to="/apply" className="kw-btn-primary w-full inline-block text-center mt-5" data-testid={`tier-apply-${i}`}>Apply</Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Apply() {
  const [f, setF] = useState({ name: "", email: "", phone: "", age: "", goals: "", experience: "", message: "" });
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/applications", { ...f, age: f.age ? parseInt(f.age) : null });
      setOk(true);
    } catch (e) { setErr(formatApiError(e)); }
  };
  if (ok) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar />
      <div className="max-w-md mx-auto px-5 py-24 text-center">
        <CheckCircle2 className="w-14 h-14 text-gold mx-auto"/>
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter mt-4" data-testid="apply-success">Application received</h1>
        <p className="text-neutral-400 mt-2">We'll be in touch within 48 hours to arrange your free consultation.</p>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar />
      <div className="max-w-xl mx-auto px-5 py-12">
        <div className="text-gold uppercase tracking-[0.3em] text-xs">Free consultation</div>
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter">Apply for coaching</h1>
        <p className="text-neutral-400 mt-2 text-sm">No obligation. We'll have an honest conversation about whether this is right for you.</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div><label className="kw-label">Full name</label><input className="kw-input" value={f.name} onChange={e=>setF({...f,name:e.target.value})} required data-testid="apply-name"/></div>
          <div><label className="kw-label">Email</label><input className="kw-input" type="email" value={f.email} onChange={e=>setF({...f,email:e.target.value})} required data-testid="apply-email"/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="kw-label">Phone</label><input className="kw-input" value={f.phone} onChange={e=>setF({...f,phone:e.target.value})} data-testid="apply-phone"/></div>
            <div><label className="kw-label">Age</label><input className="kw-input" type="number" value={f.age} onChange={e=>setF({...f,age:e.target.value})} data-testid="apply-age"/></div>
          </div>
          <div><label className="kw-label">Your goals *</label><textarea className="kw-input" rows={3} value={f.goals} onChange={e=>setF({...f,goals:e.target.value})} required placeholder="E.g. lose 15kg, build strength, lead by example for my kids" data-testid="apply-goals"/></div>
          <div><label className="kw-label">Experience with training</label><textarea className="kw-input" rows={2} value={f.experience} onChange={e=>setF({...f,experience:e.target.value})} data-testid="apply-exp"/></div>
          <div><label className="kw-label">Anything else</label><textarea className="kw-input" rows={2} value={f.message} onChange={e=>setF({...f,message:e.target.value})} data-testid="apply-msg"/></div>
          {err && <div className="text-red-400 text-sm">{err}</div>}
          <button className="kw-btn-primary w-full" data-testid="apply-submit">Submit application</button>
        </form>
      </div>
    </div>
  );
}

export function Program() {
  const [weeks, setWeeks] = useState([]);
  React.useEffect(() => { api.get("/warrior-weeks").then(r=>setWeeks(r.data)); }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar />
      <div className="max-w-5xl mx-auto px-5 py-14">
        <div className="text-gold uppercase tracking-[0.3em] text-xs">The programme</div>
        <h1 className="font-display font-black uppercase text-5xl tracking-tighter">12-Week Warrior Transformation</h1>
        <p className="text-neutral-400 mt-3 max-w-2xl">Twelve weeks of structured work. Daily habits, weekly check-ins, and a plan that respects your family, your culture and your capacity.</p>
        <div className="mt-10 space-y-2">
          {weeks.map(w => (
            <div key={w.week} className="kw-card p-4 flex items-start gap-4" data-testid={`prog-week-${w.week}`}>
              <div className="font-display font-black text-gold text-3xl w-14 shrink-0">W{w.week}</div>
              <div>
                <div className="font-display font-bold uppercase tracking-tight text-xl">{w.theme}</div>
                <div className="text-sm text-neutral-400 mt-1">{w.focus}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar />
      <div className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter">Privacy Policy</h1>
        <p className="text-neutral-400 text-sm mt-1">Kaihau Warrior Coaching · Aotearoa New Zealand</p>
        <div className="mt-6 space-y-4 text-sm text-neutral-300 leading-relaxed">
          <p>We take the privacy of your personal and health information seriously. This policy explains what we collect, why, and how we protect it, consistent with the New Zealand Privacy Act 2020 and Health Information Privacy Code.</p>
          <h2 className="font-display font-bold uppercase text-xl text-white tracking-tight mt-6">What we collect</h2>
          <p>Name, email, phone, demographics (age, sex), measurements (height, weight, waist), activity level, exercise and nutrition logs, relevant medical history that you choose to share, and optional progress photos.</p>
          <h2 className="font-display font-bold uppercase text-xl text-white tracking-tight mt-6">Why we collect it</h2>
          <p>Only to deliver coaching: calculate your calorie and macro targets, plan your training, review your progress, and support your transformation. We do not sell your data.</p>
          <h2 className="font-display font-bold uppercase text-xl text-white tracking-tight mt-6">Who can see it</h2>
          <p>Only you and your assigned coach. Progress photos are stored in secure object storage and served only through authenticated requests.</p>
          <h2 className="font-display font-bold uppercase text-xl text-white tracking-tight mt-6">Health & safety</h2>
          <p>Kaihau Warrior Coaching provides health coaching, not medical advice. We do not diagnose or treat conditions. If you have medical concerns, consult your GP. We may request GP clearance before intense training.</p>
          <h2 className="font-display font-bold uppercase text-xl text-white tracking-tight mt-6">Estimates vs results</h2>
          <p>Calorie and macro targets are estimates based on the Mifflin-St Jeor equation. They are not guarantees of weight loss. Actual results depend on consistency, sleep, stress and adherence.</p>
          <h2 className="font-display font-bold uppercase text-xl text-white tracking-tight mt-6">Your rights</h2>
          <p>You can access, correct or delete your information at any time by contacting your coach.</p>
        </div>
      </div>
    </div>
  );
}
